import { type FC, useState } from 'react';

import { useTranslation } from '@crehana/i18n';
import Calendar from '@crehana/icons/outline/Calendar';
import {
  Col,
  Container,
  type CuiOptionType,
  colors,
  Dropdown,
  Row,
  // biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
} from '@crehana/ui';

import DatePicker from '@/shared/components/DatePicker';
import IconLabel from '@/views/AdminDashboard/components/IconLabel';

import type { DateRangePickerProps } from './types';
import { dateStrToMoment, fmtDatesToForm } from './utils';

const DateRangePicker: FC<DateRangePickerProps> = ({
  title,
  description,
  dateRanges,
  isCustomOnly = false,
  withContainer = true,
  withFutureDates,
  isDark = true,
  dateRangePicker,
  startDatePicker,
  endDatePicker,
}) => {
  const { grayMain, baseLighter } = colors;
  const { languageCode } = useTranslation();
  const [customDateSectionVisible, setCustomDateSectionVisible] =
    useState(isCustomOnly);

  const iconColor = isDark ? baseLighter : grayMain;

  const handleChangeDateRange = (selected?: CuiOptionType) => {
    if (!isCustomOnly && Boolean(dateRangePicker) && selected) {
      dateRangePicker?.onChange(selected);
      if (selected.value !== 'personalized') {
        const { startDate, endDate } = fmtDatesToForm(selected.value);
        startDatePicker.onChange(startDate);
        endDatePicker.onChange(endDate);
      } else {
        setCustomDateSectionVisible(true);
        return;
      }
      setCustomDateSectionVisible(false);
    }
  };

  const SelectorHeader = () => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!title) return null;
    return (
      <Col xs={12} md={6} lg={5} className="flex flex-row">
        <IconLabel
          icon={
            <Calendar color={iconColor} size={24} className="flex-shrink-0" />
          }
          label={title}
          description={description}
        />
      </Col>
    );
  };

  const FixedRangePicker = () => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (isCustomOnly || !dateRanges || !dateRangePicker) return <></>;
    // TODO: talk with the code owner to fix this code so we can enable this rule again

    const { label, ...rest } = dateRangePicker;
    return (
      <>
        <Col xs={12} md={6} lg={3}>
          <Dropdown
            {...rest}
            full
            className="relative"
            selected={dateRangePicker?.value as CuiOptionType<string>}
            onSelected={handleChangeDateRange}
            options={dateRanges}
            isDark={isDark}
            errorText={rest.errorText || ''}
          />
        </Col>
      </>
    );
  };

  const ColWrapper: React.FC<
    React.PropsWithChildren<{ isFirst?: boolean }>
  > = ({ children, isFirst }) => {
    if (!withContainer) {
      return (
        <Col
          xs={12}
          sm={6}
          md={6}
          lg={5}
          offset={!isFirst ? { lg: 1 } : undefined}
        >
          {children}
        </Col>
      );
    }
    return (
      <Col xs={12} sm={6} md={6} lg={6}>
        {children}
      </Col>
    );
  };

  const CustomRangePicker: React.FC<React.PropsWithChildren<unknown>> = ({
    children,
  }) => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!children) return null;

    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!Array.isArray(children)) return <ColWrapper>{children}</ColWrapper>;
    return (
      <Col
        xs={12}
        md={12}
        lg={withContainer ? 8 : 12}
        className="tw-rounded tw-bg-neutral-light-200 dark:tw-bg-elevation-dark-01 tw-my-1"
      >
        <Row className="tw-p-1.6">
          {children.map((child, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: index as key is acceptable here
            <ColWrapper key={`wrapped-col-${index}`} isFirst={index === 0}>
              {child}
            </ColWrapper>
          ))}
        </Row>
      </Col>
    );
  };

  const Content = () => (
    <>
      <SelectorHeader />
      <FixedRangePicker />
      {customDateSectionVisible && (
        <CustomRangePicker>
          <DatePicker
            className="w-full"
            {...startDatePicker}
            isDark={isDark}
            langCode={languageCode}
            onSelect={startDatePicker.onChange}
            value={startDatePicker.value as string}
            limits={{
              maxDate: dateStrToMoment(
                !withFutureDates
                  ? (endDatePicker.value as string) || ''
                  : (endDatePicker.value as string) || undefined,
                endDatePicker.value ? '+1' : undefined,
              ),
            }}
          />
          <DatePicker
            className="w-full"
            {...endDatePicker}
            isDark={isDark}
            langCode={languageCode}
            onSelect={endDatePicker.onChange}
            value={endDatePicker.value as string}
            limits={{
              minDate: dateStrToMoment(
                (startDatePicker.value as string) || '',
                ((startDatePicker.value as string) && '-1') || undefined,
              ),
              maxDate: !withFutureDates ? dateStrToMoment('') : undefined,
            }}
          />
        </CustomRangePicker>
      )}
    </>
  );

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (withContainer)
    return (
      <Container type="dashboard" className="relative">
        <Row
          className={`flex-1 flex-row rounded rounded-10 ${
            isDark ? 'bg-base-main' : ' bg-white'
          } px-24 py-32`}
        >
          <Content />
        </Row>
      </Container>
    );

  return <Content />;
};

export default DateRangePicker;
